<template>
  <div id="app">
    <NavigationBar/>
    <div
      v-show="!userVerified && !$root.loading && $root.hasUser"
      class="verification-prompt"
    >
      <p class="verification-text">Please verify your email address</p>
      <p class="verification-additionals">You can only generate 1 app with an unverified account. If you've verified your account refresh this page.</p>
      <span v-show="this.resendingVerificationEmail">
        <circle6></circle6>
      </span>
      <p @click="resendVerification" :class="resendButtonStyling">Resend verification email</p>
    </div>
    <div v-if="$root.loading" class="loading-content">
      <div>
        <circle6 class="loading-indicator"></circle6>
      </div>
    </div>
    <transition v-else name="router-animation">
      <router-view class="app-content"/>
    </transition>

    <footer :class="{'hide-footer' : footerState == 1}">
      <div class="footer-content">
        <p>AppSkeletons 2019 - V 0.3.21</p>
        <div class="footer-text">
          Created with
          <img class="dev-with-logo" src="./assets/images/logos/vue-logo.png"> +
          <img class="dev-with-logo" src="./assets/images/logos/firebase-logo.png">
        </div>
        <p>
          <a href="https://medium.com/filledstacks">By Dane Mackier A.K.A FilledStacks</a>
        </p>
        <div>
          <a href="https://www.youtube.com/channel/UC2d0BYlqQCdF9lJfydl_02Q" target="_blank">
            <img
              class="dev-with-logo right-space youtube"
              src="./assets/images/logos/youtube-logo.svg"
            >
          </a>
          <a href="https://twitter.com/filledstacks" target="_blank">
            <img
              class="dev-with-logo right-space twitter"
              src="./assets/images/logos/twitter-logo.svg"
            >
          </a>
          <a href="https://github.com/filledstacks" target="_blank">
            <img
              class="dev-with-logo right-space github"
              src="./assets/images/logos/github-logo.svg"
            >
          </a>
        </div>
      </div>
    </footer>

    <modal classes="modal-container" name="info-modal" @before-open="beforeOpen">
      <h4 class="modal-title">{{ modalTitle }}</h4>
      <p class="modal-description">{{ modalMessage }}</p>
      <div class="modal-buttons">
        <button
          @click="confirmModalAction"
          v-show="showModalConfirmButton"
          class="cancel-button"
        >{{modalConfirmButton}}</button>
        <button
          @click="cancelModalAction"
          v-show="showModalCancelButton"
          class="confirm-button invert-cta"
        >{{modalCancelButton}}</button>
      </div>
    </modal>
  </div>
</template>

<script>
import firebase from "firebase";
import { Circle6 } from "vue-loading-spinner";
import { mapState, mapActions } from "vuex";
import NavigationBar from "@/components/NavigationBar.vue";

export default {
  name: "App",
  data() {
    return {
      modalTitle: "",
      modalMessage: "",
      modalPositiveCallback: {},
      modalConfirmButton: "OK",
      modalCancelButton: ""
    };
  },
  components: {
    Circle6,
    NavigationBar
  },
  computed: {
    ...mapState(["resendingVerificationEmail", "userVerified", "footerState"]),
    showModalCancelButton() {
      return this.modalCancelButton !== "";
    },
    showModalConfirmButton() {
      return this.modalConfirmButton !== "";
    },
    resendButtonStyling() {
      return {
        "resend-enabled": !this.resendingVerificationEmail,
        "resend-disabled": this.resendingVerificationEmail
      };
    }
  },
  methods: {
    ...mapActions(["resendVerificationEmail"]),
    cancelModalAction() {
      this.$modal.hide("info-modal");
    },
    confirmModalAction() {
      this.$modal.hide("info-modal");
      this.modalNegativeMessage = "";
      this.modalPositiveMessage = "OK";

      if (this.modalPositiveCallback !== {}) {
        this.modalPositiveCallback();
      }
    },

    beforeOpen(event) {
      this.modalTitle = event.params.title;
      this.modalMessage = event.params.message;
      
      if(event.params.positiveCallback) {
        this.modalPositiveCallback = event.params.positiveCallback;
      }

      console.log(JSON.stringify(event.params));

      if (event.params.confirmButton && event.params.confirmButton !== "") {
        this.modalConfirmButton = event.params.confirmButton;
      }

      if (event.params.cancelButton && event.params.cancelButton !== "") {
        this.modalCancelButton = event.params.cancelButton;
      }
    },
    resendVerification() {
      // if (!this.resendingVerificationEmail) {
      this.resendVerificationEmail();
      // }
    }
  }
};
</script>


<style lang="scss">
@import url("https://fonts.googleapis.com/css?family=Open+Sans:400,700,800");
@import "./assets/styles/styles.scss";

.hide-footer {
  display: none;
}

.youtube {
  color: #ff0a00;
}

.right-space {
  margin-right: 10px;
}

html,
body {
  box-sizing: border-box;
  background-color: $background-color;
}
*,
*:before,
*:after {
  box-sizing: inherit;
}

html {
  /* body will set it's height based on its parent, which is html */
  height: 100%;

  /* set full width as well */
  width: 100%;
}

body {
  min-height: 100%;
  margin: 0;
  padding: 0;
}

#app {
  font-family: "Open Sans", sans-serif;
  box-sizing: border-box;
  color: white;
  min-height: 100vh;
  margin: 0 auto;
}

.resend-disabled,
.resend-enabled {
  background: $dark-grey-color;
  width: fit-content;
  padding: 5px 8px;
  border-radius: 5px;
  margin-top: 8px;
}

.resend-disabled {
  cursor: default;
  color: $light-grey-color;
}

.resend-enabled {
  cursor: pointer;
  color: $primary-color;
}

.verification-additionals {
  font-size: 0.7em;
}

.verification-prompt {
  position: fixed;
  margin: 40px 40px;
  background-color: $light-grey-color;
  width: fit-content;
  padding: 10px 15px;
  border-radius: 5px;
  right: 0;
  z-index: 100;

  span {
    float: right;
  }

  .verification-text {
    font-weight: 700;
  }
}

.dev-with-logo {
  width: 15px;
  height: 15px;
  object-fit: contain;
  display: inline-block;
  margin-bottom: -5px;
}

.app-content {
  height: calc(100vh - 80px - 80px);
  padding: 40px;
  z-index: 1;
}

.loading-container {
  display: grid;
  margin-top: 20px;
}

.loading-indicator {
  justify-self: center;
}

.loading-content {
  height: calc(100vh - 80px - 80px);
  padding: 40px;
  display: grid;

  $loading-container-size: 100px;

  div {
    height: $loading-container-size;
    width: $loading-container-size;
    justify-self: center;
    align-self: center;
    background: $light-grey-color;
    border-radius: 5px;
    display: grid;
  }

  .loading-indicator {
    justify-self: center;
    align-self: center;
  }
}

.zero-margin-padding {
  margin: 0;
  padding: 0;
}

.not-available {
  opacity: 0.4;
}

$footer-height: 80px;
footer {
  height: $footer-height;
  // background: $dark-grey-color;
  width: 100vw;
  bottom: 0;
  left: 0;
  display: grid;
  grid-template-columns: 30% auto 30%;
  grid-template-areas: "contact-us app-version social-media";

  a {
    text-decoration: none;
    color: white;
    cursor: pointer;
  }
}

.footer-content {
  grid-area: app-version;
  // background: red;
  width: fit-content;
  align-self: center;
  justify-self: center;
  font-size: 10px;
  text-align: center;
}

.footer-text {
  text-align: center;
}

.body-title {
  padding: 0;
  margin: 0;
  margin-bottom: 10px;
  font-weight: 800;
  font-size: $input-title-size;
}

.body {
  padding: 0;
  margin: 0;
  margin-bottom: 10px;
  font-weight: 400;
  font-size: $input-title-size;
}

.contact-us {
  grid-area: contact-us;

  height: $footer-height;
}

.footer-title {
  font-size: 13px;
  font-weight: 700;
  padding: 0;
  margin: 0;
  margin-left: 30px;
  margin-top: 10px;
}

.footer-body {
  margin: 0;
  padding: 0;
  margin-left: 30px;
  margin-top: 7px;
  font-size: 11px;
  font-weight: 400;
}

.app-version {
  grid-area: app-version;
  font-size: 9px;
  font-weight: 400;
  display: grid;

  grid-template-rows: 1fr 1fr 1fr;
  p {
    justify-self: center;
    align-self: center;
    margin: 0;
    padding: 0;
  }

  .created-with {
    display: grid;
    grid-template-columns: 20% 80%;
  }
}

.social-media {
  grid-area: social-media;
  height: $footer-height;
}

.social-icons {
  display: grid;
  grid-template-columns: repeat(auto-fit, 50px);
  margin-top: 5px;
  margin-left: 30px;
}

.modal-container {
  background-color: $background-color;
  border-radius: 5px;

  display: grid;
  grid-template-rows: 20% 50% 30%;

  grid-template-areas:
    "title"
    "description"
    "buttons";

  button {
    width: 180px;
    height: 45px;
  }

  .loading-indicator {
    grid-area: description;
    transform: scale(2.5);
    justify-self: center;
    align-self: center;
  }
}

.cancel-button {
  background-color: rgb(236, 97, 97);
  color: white;
  margin: 0 5px;
}

.confirm-button {
  margin: 0 5px;
}

.cancel-button:hover {
  background-color: rgb(253, 175, 175);
}

.modal-buttons {
  grid-area: buttons;
  text-align: center;
}

.modal-title {
  grid-area: title;
  margin-top: 40px;
  text-align: center;
  align-self: center;
  justify-self: center;
  font-size: 30px;
}

.modal-description {
  grid-area: description;
  width: 60%;
  justify-self: center;
  align-self: center;
  text-align: center;
  font-weight: 400;
}

.logout-link {
  cursor: pointer;
}

.body-text {
  font-weight: 400;
}

$close-button-size: 20px;

.close-button {
  width: $close-button-size;
  height: $close-button-size;
  justify-self: center;
  align-self: center;
}

.close-button-wrapper {
  width: 50px;
  height: 50px;

  border-radius: calc(50px / 2);
  cursor: pointer;
  background-color: $light-grey-color;
  display: grid;
  transition: 0.3s;
}

.close-button-wrapper:hover {
  background-color: $very-light-grey-color;
}

button,
.cta {
  cursor: pointer;
  background: $primary-color;
  border: none;
  color: white;
  border-radius: 5px;
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 10px 20px;
  transition: 0.3s;
}

button:hover {
  background: $primary-color-light;
}

.invert-cta {
  color: $primary-color;
  background: white;
}

h1 {
  padding: 0;
  margin: 0;
  font-size: 65px;
  font-weight: 900;
  text-transform: uppercase;
}

.bottom-right {
  position: fixed;
  right: 70px;
  bottom: 120px;
}

h2 {
  padding: 0;
  margin: 0;
  font-size: 40px;
  font-weight: 900;
  text-transform: uppercase;
  color: #343434;
}

h3 {
  margin: 0;
  padding: 0;
  text-transform: uppercase;
  font-weight: 800;
  font-size: 2em;

  grid-area: title;
}

h4 {
  margin: 0;
  padding: 0;
  font-weight: 400;
  font-size: 1.4em;
  grid-area: title;
  margin-top: -10px;
  text-align: center;
}

p {
  margin: 0;
  padding: 0;
}

.form-field {
  margin-top: $form-field-top-margin;
}

.elements-animation-enter-active {
  animation: coming 0.25s;

  opacity: 0;
}

.elements-animation-leave-active {
  animation: going 0.25s;
}

.router-animation-enter-active {
  animation: coming 0.3s;

  animation-delay: 0.3s;
  opacity: 0;
}

.router-animation-leave-active {
  animation: going 0.3s;
}

@keyframes going {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes coming {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media only screen and (max-width: 641px) {
  footer {
    display: none;
  }

  .app-content {
    height: calc(100vh - 80px);
    z-index: 1;
  }
}
</style>
